<script setup lang="ts">
import { computed, ref } from 'vue'
import MLoading from '../loading'
import { IButtonProps } from '../types'

const {
    primary = false,
    soft = false,
    outline = false,
    ghost = false,
    link = false,
    danger = false,
    warning = false,
    fullWidth = false,
    submit = false,
    disabled = false,
    loading = false,
    sm = false,
    lg = false,
    readonly = false,
    enableLongPress = false,
} = defineProps<IButtonProps>()

const emit = defineEmits<{
    click: [e: MouseEvent | KeyboardEvent]
    longPress: [e: MouseEvent | TouchEvent]
}>()

interface Slots {
    default(): unknown

    icon(): unknown
}

const slots = defineSlots<Slots>()

const hasIcon = computed(() => !!slots.icon)
const hasDefault = computed(() => !!slots.default)

const classes = computed(() => [
    'btn',
    {
        'btn--primary': primary,
        'btn--soft': soft,
        'btn--outline': outline,
        'btn--ghost': ghost,
        'btn--link': link,
        'btn--warning': warning,
        'btn--danger': danger,
        'btn--sm': sm,
        'btn--lg': lg,
        'btn--full-width': fullWidth,
        'btn--disabled': disabled,
        'btn--loading': loading,
        'btn--icon-only': hasIcon.value && !hasDefault.value,
        'btn--readonly': readonly,
    },
])

const longPressDuration = 500
const pressTimer = ref<number | null>(null)
const isLongPressTriggered = ref(false)

const onPressStart = (e: TouchEvent | MouseEvent) => {
    if (!enableLongPress || disabled || loading || readonly) return

    isLongPressTriggered.value = false
    if (pressTimer.value !== null) {
        clearTimeout(pressTimer.value)
        pressTimer.value = null
    }
    pressTimer.value = window.setTimeout(() => {
        isLongPressTriggered.value = true
        emit('longPress', e)
        pressTimer.value = null
        if (
            window.navigator &&
            window.navigator.vibrate &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            window.navigator.vibrate(50)
        }
    }, longPressDuration)
}

const onPressEnd = () => {
    if (!enableLongPress) return

    if (pressTimer.value !== null) {
        clearTimeout(pressTimer.value)
        pressTimer.value = null
    }
}

const onTouchMove = () => {
    if (!enableLongPress) return

    onPressEnd()
}

const onClick = (e: MouseEvent): void => {
    if (disabled || loading || readonly) {
        e.preventDefault()
        return
    }

    if (!isLongPressTriggered.value) {
        if (
            window.navigator &&
            window.navigator.vibrate &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            window.navigator.vibrate(10)
        }
        emit('click', e)
    }

    onPressEnd()
    isLongPressTriggered.value = false
}

const onKeydown = (e: KeyboardEvent): void => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading && !readonly) {
        e.preventDefault()
        isLongPressTriggered.value = false
        emit('click', e)
    }
}
</script>

<template>
    <button
        :type="submit ? 'submit' : 'button'"
        :class="classes"
        :disabled="disabled || loading"
        :aria-busy="loading"
        :aria-disabled="disabled || loading || readonly"
        @click="onClick"
        @keydown="onKeydown"
        @mousedown="onPressStart"
        @touchstart="onPressStart"
        @mouseup="onPressEnd"
        @mouseleave="onPressEnd"
        @touchmove="onTouchMove"
        @touchend="onPressEnd"
        @touchcancel="onPressEnd"
    >
        <MLoading v-if="loading" white class="btn__icon" />
        <div v-else-if="hasIcon" class="btn__icon-wrapper">
            <slot name="icon" />
        </div>
        <slot />
    </button>
</template>
